import React, { useMemo, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../config/constants';
import AppIcon from './icons/AppIcon';
import './GasDistributorNewManualClientModal.css';

const emptyAddress = () => ({
  endereco: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  cep: '',
  apelido: '',
  principal: false
});

export default function GasDistributorNewManualClientModal({ projectId, onClose, onCreated }) {
  const [form, setForm] = useState({
    nome: '',
    telefone: '',
    forma_pagamento_preferida: '',
    observacoes: ''
  });
  const [enderecos, setEnderecos] = useState([{ ...emptyAddress(), principal: true }]);
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [phoneStatus, setPhoneStatus] = useState('idle'); // idle | valid | invalid
  const [phoneMessage, setPhoneMessage] = useState('');
  const [normalizedNumber, setNormalizedNumber] = useState('');
  const [lastCheckedRaw, setLastCheckedRaw] = useState('');
  const [saving, setSaving] = useState(false);

  const canSave = useMemo(() => {
    if (saving || checkingPhone) return false;
    if (!form.nome.trim()) return false;
    if (phoneStatus !== 'valid') return false;
    return true;
  }, [saving, checkingPhone, form.nome, phoneStatus]);

  const updateAddress = (idx, field, value) => {
    setEnderecos((prev) => prev.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));
  };

  const setPrincipalAddress = (idx) => {
    setEnderecos((prev) => prev.map((e, i) => ({ ...e, principal: i === idx })));
  };

  const addAddress = () => {
    setEnderecos((prev) => [...prev, emptyAddress()]);
  };

  const removeAddress = (idx) => {
    setEnderecos((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length === 0) return [{ ...emptyAddress(), principal: true }];
      if (!next.some((e) => e.principal)) next[0].principal = true;
      return next;
    });
  };

  const onPhoneChange = (value) => {
    setForm((prev) => ({ ...prev, telefone: value }));
    setPhoneStatus('idle');
    setPhoneMessage('');
    setNormalizedNumber('');
  };

  const handlePhoneBlur = async () => {
    const raw = String(form.telefone || '').trim();
    if (!raw) {
      setPhoneStatus('idle');
      setPhoneMessage('');
      setNormalizedNumber('');
      return;
    }

    if (raw === lastCheckedRaw && phoneStatus === 'valid' && normalizedNumber) {
      return;
    }

    try {
      setCheckingPhone(true);
      setPhoneStatus('idle');
      setPhoneMessage('Validando no WhatsApp...');

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || '';

      const response = await fetch(`${BACKEND_NODE_URL}/api/gas-distributor/projects/${encodeURIComponent(projectId)}/clients/check-number`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ phone: raw })
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        setPhoneStatus('invalid');
        setPhoneMessage(json?.error || 'Não foi possível validar o número');
        setNormalizedNumber('');
        return;
      }

      setLastCheckedRaw(raw);

      if (json?.valid && json?.normalized_number) {
        setPhoneStatus('valid');
        setPhoneMessage('Número confirmado no WhatsApp');
        setNormalizedNumber(String(json.normalized_number));
      } else {
        setPhoneStatus('invalid');
        setPhoneMessage(json?.error || 'Número não existe no WhatsApp');
        setNormalizedNumber('');
      }
    } catch (err) {
      setPhoneStatus('invalid');
      setPhoneMessage('Erro ao validar número');
      setNormalizedNumber('');
    } finally {
      setCheckingPhone(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!canSave) return;

    try {
      setSaving(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token || '';

      const payload = {
        nome: form.nome.trim(),
        telefone: form.telefone.trim(),
        normalized_number: normalizedNumber,
        forma_pagamento_preferida: form.forma_pagamento_preferida.trim() || null,
        observacoes: form.observacoes.trim() || null,
        enderecos: enderecos
          .map((e) => ({
            endereco: String(e.endereco || '').trim(),
            numero: String(e.numero || '').trim(),
            complemento: String(e.complemento || '').trim(),
            bairro: String(e.bairro || '').trim(),
            cidade: String(e.cidade || '').trim(),
            cep: String(e.cep || '').trim(),
            apelido: String(e.apelido || '').trim(),
            principal: !!e.principal
          }))
          .filter((e) => e.endereco)
      };

      const response = await fetch(`${BACKEND_NODE_URL}/api/gas-distributor/projects/${encodeURIComponent(projectId)}/clients/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const json = await response.json().catch(() => ({}));
      if (!response.ok || !json?.success) {
        alert(json?.error || 'Erro ao cadastrar cliente');
        return;
      }

      if (typeof onCreated === 'function') onCreated(json);
    } catch (err) {
      alert('Erro ao cadastrar cliente');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="gas-manual-client-modal-overlay" onClick={onClose}>
      <div className="gas-manual-client-modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="gas-manual-client-modal-header">
          <h2>Novo Cliente</h2>
          <button type="button" className="close-btn" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <form className="gas-manual-client-modal-body" onSubmit={handleSave}>
          <div className="field-row">
            <label>Nome *</label>
            <input
              type="text"
              value={form.nome}
              onChange={(e) => setForm((p) => ({ ...p, nome: e.target.value }))}
              placeholder="Nome do cliente"
              required
            />
          </div>

          <div className="field-row">
            <label>Telefone WhatsApp *</label>
            <div className="phone-input-wrap">
              <input
                type="text"
                value={form.telefone}
                onChange={(e) => onPhoneChange(e.target.value)}
                onBlur={handlePhoneBlur}
                placeholder="Ex: (31) 99876-5432"
                required
              />
              {checkingPhone && <span className="phone-pill checking">Verificando...</span>}
              {!checkingPhone && phoneStatus === 'valid' && <span className="phone-pill valid">OK</span>}
              {!checkingPhone && phoneStatus === 'invalid' && <span className="phone-pill invalid">Inválido</span>}
            </div>
            {!!phoneMessage && (
              <small className={`phone-message ${phoneStatus === 'valid' ? 'valid' : phoneStatus === 'invalid' ? 'invalid' : ''}`}>
                {phoneMessage}
              </small>
            )}
          </div>

          <div className="field-row">
            <label>Forma de pagamento preferida</label>
            <input
              type="text"
              value={form.forma_pagamento_preferida}
              onChange={(e) => setForm((p) => ({ ...p, forma_pagamento_preferida: e.target.value }))}
              placeholder="PIX, dinheiro, cartão..."
            />
          </div>

          <div className="field-row">
            <label>Observações</label>
            <textarea
              value={form.observacoes}
              onChange={(e) => setForm((p) => ({ ...p, observacoes: e.target.value }))}
              rows={3}
              placeholder="Informações adicionais do cliente"
            />
          </div>

          <div className="addresses-block">
            <div className="addresses-header">
              <h3>Endereços</h3>
              <button type="button" onClick={addAddress}>+ Adicionar</button>
            </div>

            {enderecos.map((end, idx) => (
              <div key={`addr-${idx}`} className="address-card">
                <div className="address-card-head">
                  <strong>Endereço {idx + 1}</strong>
                  <div className="address-actions">
                    <label>
                      <input
                        type="radio"
                        name="endereco-principal"
                        checked={!!end.principal}
                        onChange={() => setPrincipalAddress(idx)}
                      />
                      Principal
                    </label>
                    {enderecos.length > 1 && (
                      <button type="button" onClick={() => removeAddress(idx)}>Remover</button>
                    )}
                  </div>
                </div>

                <div className="address-grid">
                  <input placeholder="Endereço" value={end.endereco} onChange={(e) => updateAddress(idx, 'endereco', e.target.value)} />
                  <input placeholder="Número" value={end.numero} onChange={(e) => updateAddress(idx, 'numero', e.target.value)} />
                  <input placeholder="Complemento" value={end.complemento} onChange={(e) => updateAddress(idx, 'complemento', e.target.value)} />
                  <input placeholder="Bairro" value={end.bairro} onChange={(e) => updateAddress(idx, 'bairro', e.target.value)} />
                  <input placeholder="Cidade" value={end.cidade} onChange={(e) => updateAddress(idx, 'cidade', e.target.value)} />
                  <input placeholder="CEP" value={end.cep} onChange={(e) => updateAddress(idx, 'cep', e.target.value)} />
                  <input placeholder="Apelido (ex: Casa)" value={end.apelido} onChange={(e) => updateAddress(idx, 'apelido', e.target.value)} />
                </div>
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <button type="button" className="secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="primary" disabled={!canSave}>
              {saving ? 'Salvando...' : 'Salvar Cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
